import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';
declare module '@mui/material/styles' {
  interface TypeBackground {
    neutral: string;
  }
  interface CommonColors {
    lightgreen: string;
    darkgreen: string;
    blue: string;
    yellow: string;
    red: string;
    accentGray: string;
    lightGray: string;
    midGray: string;
    darkGray: string;
    gradientLightGray: string;
  }
  interface Palette {
    qualitative: {
      MOSTPOSITIVE: string;
      MOREPOSITIVE: string;
      POSITIVE: string;
      NEUTRALPOSITIVE: string;
      NEUTRAL: string;
      NEUTRALNEGATIVE: string;
      NEGATIVE: string;
      MORENEGATIVE: string;
      MOSTNEGATIVE: string;
    };
    diverging: {
      ONE: string;
      TWO: string;
      THREE: string;
      FOUR: string;
      FIVE: string;
      SIX: string;
      SEVEN: string;
      EIGHT: string;
      NINE: string;
    };
    landcover: {
      TREECANOPY: string;
      IMPERVIOUS: string;
      IMPERVIOUSUNDERTREECANOPY: string;
      SHRUB: string;
      WATER: string;
      BARESOIL: string;
      HERBACEOUS: string;
    };
    achromatopsia_grayscale: {
      ONE: string;
      TWO: string;
      THREE: string;
      FOUR: string;
      FIVE: string;
      SIX: string;
      SEVEN: string;
      EIGHT: string;
      NINE: string;
    };
  }
  interface PaletteOptions {
    qualitative?: {
      MOSTPOSITIVE: string;
      MOREPOSITIVE: string;
      POSITIVE: string;
      NEUTRALPOSITIVE: string;
      NEUTRAL: string;
      NEUTRALNEGATIVE: string;
      NEGATIVE: string;
      MORENEGATIVE: string;
      MOSTNEGATIVE: string;
    };
    diverging?: {
      ONE: string;
      TWO: string;
      THREE: string;
      FOUR: string;
      FIVE: string;
      SIX: string;
      SEVEN: string;
      EIGHT: string;
      NINE: string;
    };
    landcover?: {
      TREECANOPY: string;
      IMPERVIOUS: string;
      IMPERVIOUSUNDERTREECANOPY: string;
      SHRUB: string;
      WATER: string;
      BARESOIL: string;
      HERBACEOUS: string;
    };
    achromatopsia_grayscale?: {
      ONE: string;
      TWO: string;
      THREE: string;
      FOUR: string;
      FIVE: string;
      SIX: string;
      SEVEN: string;
      EIGHT: string;
      NINE: string;
    };
  }
}

// BASE PALETTE CONSTANTS

const PG_PRODUCT_GREEN = '#578128';
const PG_PRODUCT_SECONDARY_GREEN = '#365000';
const PG_PRODUCT_BLUE = '#0062a3';
const PG_PRODUCT_YELLOW = '#ffd450';
const PG_PRODUCT_RED = '#D03D27';
const PG_PRODUCT_LIGHT_GRAY = '#949494';
const PG_PRODUCT_MID_GRAY = '#757575';
const PG_PRODUCT_DARK_GRAY = '#555555';
const PG_PRODUCT_ACCENT_GRAY = '#e9e9e9';

// GRADIENT CONSTANTS
const PG_WHITE_TO_LIGHT_GRAY_GRADIENT = `linear-gradient(to bottom, #ffffff, ${PG_PRODUCT_ACCENT_GRAY})`;

// ACHROMATOPSIA GRAYSCALE CONSTANTS (9 shades from white to black)

const PG_ACHROMATOPSIA_ONE = '#FFFFFF';
const PG_ACHROMATOPSIA_TWO = '#DBDBDB';
const PG_ACHROMATOPSIA_THREE = '#B8B8B8';
const PG_ACHROMATOPSIA_FOUR = PG_PRODUCT_LIGHT_GRAY;
const PG_ACHROMATOPSIA_FIVE = PG_PRODUCT_MID_GRAY;
const PG_ACHROMATOPSIA_SIX = '#666666';
const PG_ACHROMATOPSIA_SEVEN = PG_PRODUCT_DARK_GRAY;
const PG_ACHROMATOPSIA_EIGHT = '#333333';
const PG_ACHROMATOPSIA_NINE = '#000000';

// QUALITATIVE GRADIENT CONSTANTS

const PG_QUALITATIVE_MOST_POSITIVE = '#448F6B';
const PG_QUALITATIVE_MORE_POSITIVE = '#60C693';
const PG_QUALITATIVE_POSITIVE = '#BEDB5B';
const PG_QUALITATIVE_NEUTRAL_POSITIVE = '#ECE662';
const PG_QUALITATIVE_NEUTRAL = '#DEB769';
const PG_QUALITATIVE_NEUTRAL_NEGATIVE = '#CD956B';
const PG_QUALITATIVE_NEGATIVE = '#C67F7D';
const PG_QUALITATIVE_MORE_NEGATIVE = '#AD4691';
const PG_QUALITATIVE_MOST_NEGATIVE = '#950EA5';

// DIVERGING GRADIENT CONSTANTS

const PG_DIVERGING_ONE = '#4D9221';
const PG_DIVERGING_TWO = '#7FBC41';
const PG_DIVERGING_THREE = '#B8E186';
const PG_DIVERGING_FOUR = '#E6F5D0';
const PG_DIVERGING_FIVE = '#F7F7F7';
const PG_DIVERGING_SIX = '#FDE0EF';
const PG_DIVERGING_SEVEN = '#F1B6DA';
const PG_DIVERGING_EIGHT = '#DE77AE';
const PG_DIVERGING_NINE = '#C51B7D';

// LANDCOVER CONSTANTS

const PG_LANDCOVER_IMPERVIOUS = '#696969';
const PG_LANDCOVER_IMPERVIOUS_UNDER_TREE_CANOPY = '#7D358F';
const PG_LANDCOVER_SHRUB = '#FF7F50';
const PG_LANDCOVER_WATER = '#2495F4';
const PG_LANDCOVER_BARE_SOIL = '#CD956B';
const PG_LANDCOVER_HERBACEOUS = '#A4E323';

// SETUP COLORS

const PRIMARY = {
  main: PG_PRODUCT_GREEN,
  contrastText: '#fff',
};

const SECONDARY = {
  main: PG_PRODUCT_SECONDARY_GREEN,
  contrastText: '#fff',
};

const INFO = {
  main: PG_PRODUCT_BLUE,
  dark: '#004370',
  contrastText: '#fff',
};

const SUCCESS = {
  main: PG_PRODUCT_GREEN,
  contrastText: '#fff',
};

const WARNING = {
  main: PG_PRODUCT_YELLOW,
  dark: '#E0A800',
  contrastText: '#000',
};

const ERROR = {
  main: PG_PRODUCT_RED,
  contrastText: '#fff',
};

const QUALITATIVEGRADIENT = {
  MOSTPOSITIVE: PG_QUALITATIVE_MOST_POSITIVE,
  MOREPOSITIVE: PG_QUALITATIVE_MORE_POSITIVE,
  POSITIVE: PG_QUALITATIVE_POSITIVE,
  NEUTRALPOSITIVE: PG_QUALITATIVE_NEUTRAL_POSITIVE,
  NEUTRAL: PG_QUALITATIVE_NEUTRAL,
  NEUTRALNEGATIVE: PG_QUALITATIVE_NEUTRAL_NEGATIVE,
  NEGATIVE: PG_QUALITATIVE_NEGATIVE,
  MORENEGATIVE: PG_QUALITATIVE_MORE_NEGATIVE,
  MOSTNEGATIVE: PG_QUALITATIVE_MOST_NEGATIVE,
};

const DIVERGINGGRADIENT = {
  ONE: PG_DIVERGING_ONE,
  TWO: PG_DIVERGING_TWO,
  THREE: PG_DIVERGING_THREE,
  FOUR: PG_DIVERGING_FOUR,
  FIVE: PG_DIVERGING_FIVE,
  SIX: PG_DIVERGING_SIX,
  SEVEN: PG_DIVERGING_SEVEN,
  EIGHT: PG_DIVERGING_EIGHT,
  NINE: PG_DIVERGING_NINE,
};

const LANDCOVER = {
  TREECANOPY: PG_PRODUCT_SECONDARY_GREEN,
  IMPERVIOUS: PG_LANDCOVER_IMPERVIOUS,
  IMPERVIOUSUNDERTREECANOPY: PG_LANDCOVER_IMPERVIOUS_UNDER_TREE_CANOPY,
  SHRUB: PG_LANDCOVER_SHRUB,
  WATER: PG_LANDCOVER_WATER,
  BARESOIL: PG_LANDCOVER_BARE_SOIL,
  HERBACEOUS: PG_LANDCOVER_HERBACEOUS,
};

const ACHROMATOPSIA_GRAYSCALE = {
  ONE: PG_ACHROMATOPSIA_ONE,
  TWO: PG_ACHROMATOPSIA_TWO,
  THREE: PG_ACHROMATOPSIA_THREE,
  FOUR: PG_ACHROMATOPSIA_FOUR,
  FIVE: PG_ACHROMATOPSIA_FIVE,
  SIX: PG_ACHROMATOPSIA_SIX,
  SEVEN: PG_ACHROMATOPSIA_SEVEN,
  EIGHT: PG_ACHROMATOPSIA_EIGHT,
  NINE: PG_ACHROMATOPSIA_NINE,
};

const COMMON = {
  common: {
    black: '#000000',
    white: '#ffffff',
    lightgreen: PG_PRODUCT_GREEN,
    darkgreen: PG_PRODUCT_SECONDARY_GREEN,
    blue: PG_PRODUCT_BLUE,
    yellow: PG_PRODUCT_YELLOW,
    red: PG_PRODUCT_RED,
    accentGray: PG_PRODUCT_ACCENT_GRAY,
    lightGray: PG_PRODUCT_LIGHT_GRAY,
    midGray: PG_PRODUCT_MID_GRAY,
    darkGray: PG_PRODUCT_DARK_GRAY,
    gradientLightGray: PG_WHITE_TO_LIGHT_GRAY_GRADIENT,
  },
  landcover: LANDCOVER,
  qualitative: QUALITATIVEGRADIENT,
  diverging: DIVERGINGGRADIENT,
  achromatopsia_grayscale: ACHROMATOPSIA_GRAYSCALE,
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  divider: PG_PRODUCT_MID_GRAY,
  action: {
    hover: alpha(PG_PRODUCT_MID_GRAY, 0.08),
    selected: alpha(PG_PRODUCT_MID_GRAY, 0.16),
    disabled: alpha(PG_PRODUCT_MID_GRAY, 0.8),
    disabledBackground: alpha(PG_PRODUCT_MID_GRAY, 0.24),
    focus: alpha(PG_PRODUCT_MID_GRAY, 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export function palette() {
  return {
    ...COMMON,
    text: {
      primary: PG_PRODUCT_DARK_GRAY,
      secondary: PG_PRODUCT_MID_GRAY,
      disabled: PG_PRODUCT_MID_GRAY,
    },
    background: { paper: '#fff', default: '#fff', neutral: PG_PRODUCT_LIGHT_GRAY },
    action: {
      ...COMMON.action,
      active: PG_PRODUCT_MID_GRAY,
    },
  } as const;
}
